import { theme } from 'antd';
import type { ThemeConfig } from 'antd';
import { getDesignTokens, getTokenPalette, appFontFamily, appFontFamilyMono } from './design/tokens';
import type { ColorMode } from './theme/themeMode';

type HorizonPalette = ReturnType<typeof getTokenPalette> & {
  radiusField: number;
  radiusButton: number;
  radiusCard: number;
  controlHeight: number;
  fontSize: number;
  shadow1: string;
  shadow2: string;
};

function buildThemeConfig(palette: HorizonPalette, mode: ColorMode): ThemeConfig {
  return {
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: palette.brand,
      colorInfo: palette.info,
      colorSuccess: palette.success,
      colorWarning: palette.warning,
      colorError: palette.error,
      colorLink: palette.link,
      colorText: palette.text,
      colorTextSecondary: palette.label,
      colorTextPlaceholder: palette.label,
      colorBorder: palette.border,
      colorBorderSecondary: palette.border,
      colorBgLayout: palette.background,
      colorBgContainer: palette.surface,
      colorBgElevated: palette.surface,
      colorFillAlter: palette.hover,
      colorFillSecondary: palette.hover,
      controlItemBgHover: palette.hover,
      controlItemBgActive: palette.active,
      controlOutline: `0 0 0 2px ${palette.focusRing}`,
      fontFamily: appFontFamily,
      fontFamilyCode: appFontFamilyMono,
      fontSize: 14,
      borderRadius: palette.radiusField,
      borderRadiusSM: 6,
      borderRadiusLG: palette.radiusCard,
      controlHeight: palette.controlHeight,
      motion: true,
      motionDurationMid: '0.2s',
      motionDurationSlow: '0.3s',
      lineWidth: 1,
      lineWidthFocus: 2,
      boxShadow: palette.shadow1,
      boxShadowSecondary: palette.shadow2,
    },
    components: {
      Button: {
        colorPrimary: palette.brand,
        colorPrimaryHover: palette.highlight,
        colorPrimaryActive: palette.highlight,
        defaultBorderColor: palette.border,
        defaultColor: palette.text,
        borderRadius: palette.radiusButton,
        controlHeight: palette.controlHeight,
        primaryShadow: 'none',
        defaultShadow: 'none',
        dangerShadow: 'none',
        fontWeight: 600,
      },
      Input: {
        colorBorder: palette.fieldBorder,
        hoverBorderColor: palette.highlight,
        activeBorderColor: palette.focus,
        borderRadius: palette.radiusField,
        controlHeight: palette.controlHeight,
        paddingInline: 12,
      },
      Select: {
        colorBorder: palette.fieldBorder,
        borderRadius: palette.radiusField,
        controlHeight: palette.controlHeight,
      },
      DatePicker: {
        colorBorder: palette.fieldBorder,
        borderRadius: palette.radiusField,
        controlHeight: palette.controlHeight,
      },
      InputNumber: {
        colorBorder: palette.fieldBorder,
        borderRadius: palette.radiusField,
        controlHeight: palette.controlHeight,
      },
      Form: {
        labelColor: palette.label,
        labelFontSize: palette.fontSize,
        itemMarginBottom: 16,
        verticalLabelPadding: '0 0 4px',
      },
      Card: {
        borderRadiusLG: palette.radiusCard,
        paddingLG: 20,
      },
      Table: {
        borderRadius: palette.radiusCard,
        headerBg: palette.hover,
        headerColor: palette.text,
        rowHoverBg: palette.hover,
      },
      Menu: {
        itemHeight: 36,
        itemMarginBlock: 2,
        itemSelectedBg: palette.selectedBg,
        itemSelectedColor: palette.link,
        itemHoverBg: palette.hover,
        itemColor: palette.text,
        groupTitleColor: palette.label,
        subMenuItemBg: palette.surface,
      },
      Layout: {
        bodyBg: palette.background,
        siderBg: palette.shell,
        headerBg: palette.shell,
        footerBg: palette.background,
      },
      Modal: {
        borderRadiusLG: palette.radiusCard,
        contentBg: palette.surface,
        headerBg: palette.surface,
        footerBg: palette.surface,
        titleColor: palette.text,
      },
      Drawer: {
        borderRadiusLG: 0,
        colorBgElevated: palette.surface,
      },
      Breadcrumb: {
        fontSize: 12,
        itemColor: palette.label,
        linkColor: palette.link,
        separatorColor: palette.label,
      },
      Typography: {
        titleMarginBottom: 8,
        titleMarginTop: 0,
      },
    },
  };
}

export function createAppTheme(mode: ColorMode): ThemeConfig {
  const tokens = getDesignTokens(mode);
  const palette = getTokenPalette(tokens);
  const enriched: HorizonPalette = {
    ...palette,
    radiusField: tokens.radius.field,
    radiusButton: tokens.radius.button,
    radiusCard: tokens.radius.lg,
    controlHeight: 40,
    fontSize: 14,
    shadow1: tokens.shadow.level1,
    shadow2: tokens.shadow.level2,
  };
  return buildThemeConfig(enriched, mode);
}

/** @deprecated Use createAppTheme('light') or ThemeProvider */
export const appTheme: ThemeConfig = createAppTheme('light');
